import {
  SourcingCurrencyStats,
  SourcingSetting,
  SourcingSpendStats,
} from 'api-clients/monolith';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  omitAutomationBannerDismissals,
  useAutomationBannerState,
} from '@/components/pages/channels/automation-status-banner/use-automation-banner-state';
import { ChevronRightIcon } from '@/components/icons/chevron-right';
import { CloseIcon } from '@/components/icons/close';
import { ExclamationIcon } from '@/components/icons/exclamation';
import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Typography } from '@/components/ui/typography';
import { useBudgetTargetSignal } from '@/contexts/budget-target-signal-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useUpdateHiringGoalTargets } from '@/hooks/use-update-hiring-goal-targets';
import { Loader2 } from 'lucide-react';

interface BudgetTargetPopoverProps {
  targetBudget: number | null;
  budgetStats: SourcingCurrencyStats;
  spendStats: SourcingSpendStats;
  automation: SourcingSetting | null;
  isReadOnly?: boolean;
}

function BreakdownRow({
  label,
  value,
  valueClassName,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="border-fx-neutral-200 flex w-full items-center justify-between border-b p-2 last:border-b-0">
      <div className="flex items-center gap-2">
        <Typography variant="small1" className="text-fx-neutral-600">
          {label}
        </Typography>
      </div>
      <Typography
        variant="small2"
        className={valueClassName ?? 'text-fx-neutral-600'}
      >
        {value}
      </Typography>
    </div>
  );
}

/**
 * Max-budget target editor for the Spend & Cost card. Also listens for the
 * automation banner's "Update Budget" signal so it can auto-open when a
 * paused-for-budget campaign needs a new target.
 */
export function BudgetTargetPopover({
  targetBudget,
  budgetStats,
  spendStats,
  automation,
  isReadOnly = false,
}: BudgetTargetPopoverProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const { formatCurrency } = useCurrencySymbol();
  const { sourceSettingsPath } = useNavigationPaths();
  const { setAutomationBannerClosed } = useAutomationBannerState();
  const { budgetTargetOpenSignal } = useBudgetTargetSignal();

  const [open, setOpen] = useState(false);
  const [maxBudget, setMaxBudget] = useState('');

  const automationEnabled = automation?.budget_automation_enabled ?? false;

  const resetFromTargetBudget = useCallback(() => {
    setMaxBudget(targetBudget != null ? String(targetBudget) : '');
  }, [targetBudget]);

  useEffect(() => {
    resetFromTargetBudget();
  }, [resetFromTargetBudget]);

  // Discard any unsaved edits from a previous open — always start from the
  // latest API-derived value rather than whatever was left in state.
  const handleOpenChange = (next: boolean) => {
    if (next) resetFromTargetBudget();
    setOpen(next);
  };

  useEffect(() => {
    if (budgetTargetOpenSignal === 0 || isReadOnly) return;
    resetFromTargetBudget();
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetTargetOpenSignal]);

  const { mutate: saveTarget, isPending } = useUpdateHiringGoalTargets({
    successMessage: intl.formatMessage({
      defaultMessage: 'Target budget updated successfully!',
      description: 'Success toast after saving the target budget',
    }),
    errorMessage: intl.formatMessage({
      defaultMessage: 'Failed to update the target budget!',
      description: 'Error toast when saving the target budget fails',
    }),
    onSuccess: () => {
      setOpen(false);
      if (openingSlug) {
        setAutomationBannerClosed(prev =>
          omitAutomationBannerDismissals(prev, openingSlug, [
            'target_budget_breached',
            'target_budget_met',
          ]),
        );
      }
    },
  });

  const handleSave = () => {
    const numBudget = parseFloat(maxBudget);

    saveTarget({ openingSlug, requestBody: { budget_allotted: numBudget } });
  };

  if (isReadOnly) return null;

  const committed = spendStats.committed ?? 0;
  const campaignBudget = budgetStats.expected ?? 0;
  const numMaxBudget = parseFloat(maxBudget);
  const hasMaxBudget = !!maxBudget && numMaxBudget > 0;
  const exceedsMax = hasMaxBudget && campaignBudget > numMaxBudget;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="link" size="link">
          {targetBudget != null ? (
            <FormattedMessage
              defaultMessage="Target budget: {amount}"
              description="Trigger: shows the current target budget amount for this opening"
              values={{ amount: formatCurrency(targetBudget) }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="+ Add target budget"
              description="Trigger: set a target budget for this opening"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-fx-neutral-200 flex w-[345px] flex-col gap-2 rounded-lg p-3 shadow-lg"
      >
        <div className="flex w-full items-center justify-between">
          <Typography variant="subtitle2" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Set a target budget for this opening"
              description="Popover title: setting a maximum spend target"
            />
          </Typography>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={intl.formatMessage({
              defaultMessage: 'Close',
              description: 'Close button: dismiss the target budget popover',
            })}
            className="text-fx-neutral-400 hover:text-fx-neutral-600 shrink-0"
          >
            <CloseIcon className="size-4" />
          </button>
        </div>

        <div className="border-fx-neutral-200 w-full border-b" />

        <div className="flex w-full flex-col gap-3">
          {automationEnabled ? (
            <div className="bg-fx-green-50 flex w-full items-start gap-2 rounded-lg p-2">
              <LightningBoltIcon className="text-fx-green mt-0.5 size-4 shrink-0" />
              <Typography variant="small1" className="!text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="<b>Budget Automation is on.</b> Campaigns pause automatically as spend nears this cap. Changing it here caps total reporting only — it won't change individual campaign budgets."
                  description="Banner: budget automation is currently enabled for this opening"
                  values={{ b: (chunks: ReactNode) => <b>{chunks}</b> }}
                />
              </Typography>
            </div>
          ) : (
            <div className="bg-fx-amber-50 flex w-full items-start gap-2 rounded-lg p-2">
              <ExclamationIcon className="text-fx-amber mt-0.5 size-4 shrink-0" />
              <div className="flex flex-col gap-2">
                <Typography
                  variant="small1"
                  className="!text-fx-neutral-600 font-bold"
                >
                  <FormattedMessage
                    defaultMessage="Budget automation is off"
                    description="Banner title: budget automation is currently disabled for this opening"
                  />
                </Typography>
                <Typography variant="small1" className="text-fx-neutral-600">
                  <FormattedMessage
                    defaultMessage="Maximum budget can't pause campaigns until automation is on."
                    description="Banner body: max budget has no effect while automation is off"
                  />
                </Typography>
                <Link
                  to={`${sourceSettingsPath}/automation`}
                  className="text-fx-amber flex items-center gap-1 hover:underline"
                >
                  <Typography
                    variant="small1"
                    className="text-fx-amber leading-4"
                  >
                    <FormattedMessage
                      defaultMessage="Enable in Settings"
                      description="Link: navigate to the automation settings page"
                    />
                  </Typography>
                  <ChevronRightIcon className="text-fx-amber size-4 shrink-0" />
                </Link>
              </div>
            </div>
          )}

          <div className="flex w-full flex-col gap-1">
            <Input
              id="budget-target-max-budget"
              isNumber
              readOnly={!automationEnabled}
              description={intl.formatMessage({
                defaultMessage: 'Maximum budget',
                description:
                  'Input label: the max total budget for this opening',
              })}
              placeholderLabel="$"
              placeholder={intl.formatMessage({
                defaultMessage: 'e.g. 250',
                description: 'Placeholder: example max budget amount',
              })}
              value={maxBudget}
              onChange={e => setMaxBudget(e.target.value)}
            />
            {!automationEnabled && (
              <Typography variant="small1" className="text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="Available after you turn on automation."
                  description="Feedback: the max budget field is inert until automation is enabled"
                />
              </Typography>
            )}
          </div>

          <div className="border-fx-neutral-200 flex w-full flex-col rounded-lg border">
            <BreakdownRow
              label={
                <FormattedMessage
                  defaultMessage="Actual spend"
                  description="Breakdown row: amount actually spent so far"
                />
              }
              value={formatCurrency(budgetStats.actual ?? 0)}
            />
            <BreakdownRow
              label={
                <FormattedMessage
                  defaultMessage="Committed spend"
                  description="Breakdown row: spend committed to channels but not yet paid"
                />
              }
              value={formatCurrency(committed)}
            />
            <BreakdownRow
              label={
                <FormattedMessage
                  defaultMessage="Total campaign budget"
                  description="Breakdown row: sum of all per-campaign budget allocations"
                />
              }
              value={formatCurrency(budgetStats.expected ?? 0)}
              valueClassName={
                exceedsMax && automationEnabled ? 'text-fx-rose' : ''
              }
            />
            <div className="bg-fx-neutral-50 flex w-full items-center justify-between rounded-b-lg p-2">
              <div className="flex items-center gap-2">
                <Typography variant="small2" className="!text-fx-neutral-600">
                  <FormattedMessage
                    defaultMessage="Max budget"
                    description="Breakdown row: the max budget currently entered in the form"
                  />
                </Typography>
              </div>
              <Typography
                variant="small2"
                className={
                  hasMaxBudget
                    ? '!text-fx-neutral-600'
                    : '!text-fx-neutral-600 italic'
                }
              >
                {hasMaxBudget ? (
                  formatCurrency(numMaxBudget)
                ) : (
                  <FormattedMessage
                    defaultMessage="Not set"
                    description="Breakdown row: no max budget has been entered yet"
                  />
                )}
              </Typography>
            </div>
          </div>

          {exceedsMax && automationEnabled && (
            <Typography variant="small1" className="!text-fx-rose">
              <FormattedMessage
                defaultMessage="Max budget is below total spend — campaigns would pause immediately."
                description="Warning: entered max budget is lower than what's already spent"
              />
            </Typography>
          )}

          <div className="flex w-full items-center justify-between">
            <Button
              variant="secondary"
              className="h-10"
              onClick={() => setOpen(false)}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel button: dismiss the target budget popover"
              />
            </Button>
            <Button className="h-10" onClick={handleSave} disabled={isPending}>
              {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              <FormattedMessage
                defaultMessage="Save"
                description="Save button: persist the target budget"
              />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
